import { dehydrate, hydrate, type DehydratedState, type QueryClient, type QueryKey } from '@tanstack/react-query';
import { meKey } from '@/features/auth/api';
import type { LocalMessage, LocalPage } from '@/features/workspace/localMessages';
import { clearStates } from './pins';
import { openKv, type Kv } from './kv';

/**
 * Копия данных на устройстве: последние переписки, документы, календарь, задачи и файлы.
 * Хранится в IndexedDB целиком (TanStack Query знает, как её вернуть), стирается при выходе.
 * Секретные чаты не сохраняются никогда: их смысл в том, что на устройстве ничего не остаётся.
 */
const KEY = 'snapshot';
/** Сколько последних сообщений беседы оставляем на устройстве. */
export const KEEP_MESSAGES = 60;
const ROOTS: ReadonlySet<string> = new Set(['ws', 'kanban']);
/**
 * Что можно держать на диске — белым списком: всё остальное (записи и ключи Пассворка, секретные ссылки, ассистент,
 * администрирование, сохранённое и отправленное, где могут быть секретные чаты) на устройство не попадает.
 */
const ALLOWED: ReadonlySet<string> = new Set(['sidebar', 'members', 'directory', 'details', 'messages', 'thread', 'pins', 'files', 'bookmarks', 'todos', 'unreads', 'docs', 'calendar']);
/** Разделы с данными беседы: третьим элементом ключа идёт её номер. */
const PER_CONVERSATION: ReadonlySet<string> = new Set(['messages', 'details', 'pins', 'files', 'bookmarks']);

let kv: Kv<DehydratedState> | null = null;
const store = (): Kv<DehydratedState> => (kv ??= openKv<DehydratedState>('chat-cache'));

/** Для тестов: подменить хранилище. */
export function useSnapshotStore(next: Kv<DehydratedState> | null): void {
  kv = next;
}

function secretConversations(client: QueryClient): Set<string> {
  const secret = new Set<string>();
  for (const [key, data] of client.getQueriesData<{ conversations?: readonly { id: string; secretUntil?: string | null; secretLink?: boolean }[] }>({ queryKey: ['ws'] })) {
    if (key[2] !== 'sidebar') continue;
    for (const c of data?.conversations ?? []) if (c.secretUntil != null || c.secretLink === true) secret.add(c.id);
  }
  return secret;
}

export function isPersistable(key: QueryKey, secret: ReadonlySet<string>, data?: unknown): boolean {
  if (key[0] === meKey[0]) return true;
  const root = key[0];
  if (typeof root !== 'string' || !ROOTS.has(root)) return false;
  const section = key[2];
  if (root === 'ws' && !(typeof section === 'string' && ALLOWED.has(section))) return false;
  if (root === 'ws' && typeof section === 'string' && PER_CONVERSATION.has(section)) {
    const cid = key[3];
    if (typeof cid === 'string' && secret.has(cid)) return false;
  }
  // Тред секретного чата: беседа корня.
  if (key[2] === 'thread' && typeof data === 'object' && data !== null) {
    const root: unknown = Reflect.get(data, 'root');
    const cid: unknown = typeof root === 'object' && root !== null ? Reflect.get(root, 'conversationId') : null;
    if (typeof cid === 'string' && secret.has(cid)) return false;
  }
  return true;
}

function isPage(data: unknown): data is LocalPage {
  return typeof data === 'object' && data !== null && Array.isArray(Reflect.get(data, 'messages'));
}

/** В копию попадают последние сообщения; всё, что старше, подгрузится, когда появится сеть. */
export function trimPage(page: LocalPage): LocalPage {
  if (page.messages.length <= KEEP_MESSAGES) return page;
  return { ...page, messages: page.messages.slice(-KEEP_MESSAGES), hasMoreBefore: true };
}

export function buildSnapshot(client: QueryClient): DehydratedState {
  const secret = secretConversations(client);
  return dehydrate(client, {
    shouldDehydrateQuery: (q) => q.state.status === 'success' && isPersistable(q.queryKey, secret, q.state.data),
    shouldDehydrateMutation: () => false,
  });
}

/** Сообщение, которое не успели отправить до закрытия страницы: в очереди — ждёт сеть, иначе — не отправлено. */
export function settlePending(message: LocalMessage, queued: ReadonlySet<string>): LocalMessage {
  if (message.status !== 'sending' && message.status !== 'queued') return message;
  return { ...message, status: message.clientId !== undefined && queued.has(message.clientId) ? 'queued' : 'failed' };
}

function settlePage(page: LocalPage, queued: ReadonlySet<string>): LocalPage {
  return { ...page, messages: page.messages.map((m) => settlePending(m, queued)) };
}

/** Вернуть копию в кэш. Вызывается один раз до первого показа страницы. */
export async function restoreSnapshot(client: QueryClient, queued: ReadonlySet<string>): Promise<boolean> {
  const saved = await store().get(KEY);
  if (saved === undefined) return false;
  hydrate(client, saved);
  for (const [key, data] of client.getQueriesData<LocalPage>({ queryKey: ['ws'] })) {
    if (key[2] === 'messages' && isPage(data)) client.setQueryData(key, settlePage(data, queued));
  }
  return true;
}

export async function saveSnapshot(client: QueryClient): Promise<void> {
  const state = buildSnapshot(client);
  // Беседы режем по месту: в живом кэше остаётся всё, в копии — последние сообщения.
  const queries = state.queries.map((q) => (q.queryKey[2] === 'messages' && isPage(q.state.data) ? { ...q, state: { ...q.state, data: trimPage(q.state.data) } } : q));
  await store().set(KEY, { ...state, queries });
}

/** Стирает всю копию на устройстве: общую и историю бесед «Доступно офлайн». */
export async function clearSnapshot(): Promise<void> {
  // Знаки «скачиваем» у бесед гаснут сразу, не дожидаясь файла со скачиванием.
  clearStates();
  await Promise.all([store().clear(), import('./history').then((m) => m.clearHistory())]);
}

/**
 * Убрать из кэша всё, кроме «кто я»: данные другого человека не должны переживать смену учётки.
 * Запросы, которые идут прямо сейчас, остаются: ответ придёт уже в новом сеансе, а убранный запрос
 * оставил бы страницу ждать ответа вечно.
 */
export function purgeUserData(client: QueryClient): void {
  client.removeQueries({ predicate: (q) => q.queryKey[0] !== meKey[0] && q.state.fetchStatus !== 'fetching' });
}

export function meId(client: QueryClient): string | null {
  const me: unknown = client.getQueryData(meKey);
  const user: unknown = typeof me === 'object' && me !== null ? Reflect.get(me, 'user') : null;
  const id: unknown = typeof user === 'object' && user !== null ? Reflect.get(user, 'id') : null;
  return typeof id === 'string' ? id : null;
}

export interface SnapshotHooks {
  /** Сеанс закончился (401 или выход): копия стёрта, очередь остаётся — сообщения уйдут после входа. */
  readonly onSignedOut: () => void;
  /** Пришёл «я» с сервера: очередь чужих сообщений убирается. */
  readonly onUser: (userId: string) => void;
}

/** Сохранять копию не чаще раза в столько: сборка и запись снимка — заметная работа на главном потоке. */
export const SNAPSHOT_GAP_MS = 20_000;
/** Первое сохранение после изменения — не раньше чем через столько (пачка ответов при входе пишется одной записью). */
const SNAPSHOT_DELAY_MS = 3000;

/** Запуск в свободную минуту браузера; где requestIdleCallback нет (Safari), — следующим таймером. */
function whenIdle(cb: () => void): () => void {
  if (typeof window.requestIdleCallback !== 'function') {
    const id = window.setTimeout(cb, 0);
    return () => {
      window.clearTimeout(id);
    };
  }
  const id = window.requestIdleCallback(cb, { timeout: 5000 });
  return () => {
    window.cancelIdleCallback(id);
  };
}

/**
 * Сохраняем копию не чаще раза в 20 секунд, в свободную минуту браузера; при уходе со страницы (вкладка скрыта, pagehide) —
 * сразу. Окончание сеанса стирает её.
 */
export function persistSnapshot(client: QueryClient, hooks: SnapshotHooks): () => void {
  let timer: number | null = null;
  let cancelIdle: (() => void) | null = null;
  let lastSave = 0;
  let owner = meId(client);
  const cancel = () => {
    if (timer !== null) window.clearTimeout(timer);
    timer = null;
    cancelIdle?.();
    cancelIdle = null;
  };
  const flush = () => {
    cancel();
    lastSave = Date.now();
    void saveSnapshot(client);
  };
  const schedule = () => {
    if (timer !== null || cancelIdle !== null) return;
    const wait = Math.max(SNAPSHOT_DELAY_MS, lastSave + SNAPSHOT_GAP_MS - Date.now());
    timer = window.setTimeout(() => {
      timer = null;
      cancelIdle = whenIdle(flush);
    }, wait);
  };
  const unsubscribe = client.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated' && event.type !== 'added') return;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TanStack отдаёт ключ как any
    const key: QueryKey = event.query.queryKey;
    if (key[0] === meKey[0]) {
      if (event.query.state.data === null) {
        cancel();
        void clearSnapshot();
        // Не входил никто — чистить нечего, а запросы открытой страницы без входа (запись на встречу) не трогаем.
        if (owner !== null) purgeUserData(client);
        owner = null;
        hooks.onSignedOut();
        return;
      }
      const id = meId(client);
      if (id !== null && id !== owner) {
        // Вошёл другой человек: копия прежнего не должна ни остаться на диске, ни показаться ему.
        if (owner !== null) {
          void clearSnapshot();
          purgeUserData(client);
        }
        owner = id;
        hooks.onUser(id);
      }
    }
    if (event.query.state.status !== 'success') return;
    schedule();
  });
  const leave = () => {
    if (meId(client) !== null) flush();
  };
  const hide = () => {
    if (document.visibilityState === 'hidden') leave();
  };
  document.addEventListener('visibilitychange', hide);
  window.addEventListener('pagehide', leave);
  return () => {
    unsubscribe();
    document.removeEventListener('visibilitychange', hide);
    window.removeEventListener('pagehide', leave);
    cancel();
  };
}
